/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, inject } from '@angular/core';
import { FileUploaderComponent } from '../shared/components/file-uploader/file-uploader.component';
import {
  InfoComponent,
  InfoComponentData,
} from '../shared/components/info/info.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BreadcrumbsComponent,
  BreadcrumbsItems,
} from '../core/components/breadcrumbs/breadcrumbs.component';
import { switchMap, filter, Observable, expand, delayWhen, timer } from 'rxjs';
import { ObjectDetectionService } from '../shared/services/object-detection-api.service';
import { OptionsComponent } from './components/options/options.component';
import { DetectionOptions } from './components/options/options.interface';
import { SeoService } from '../core/services/seo/seo.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-object-detection',
  standalone: true,
  imports: [
    FileUploaderComponent,
    InfoComponent,
    TranslateModule,
    BreadcrumbsComponent,
    OptionsComponent,
  ],
  templateUrl: './object-detection.component.html',
  styleUrl: './object-detection.component.css',
})
export class ObjectDetectionComponent {
  public fileUrl: string | null = null;

  private uploadedFile: any;

  private options: DetectionOptions | null = null;

  breadcrumbs: BreadcrumbsItems[] = [
    { caption: 'Aspose Products', url: '#' },
    { caption: 'Imaging apps', url: '#' },
  ];

  howTo: InfoComponentData = {
    title: 'instructions.stepsTitle',
    steps: [
      'instructions.step1',
      'instructions.step2',
      'instructions.step3',
      'instructions.step4',
    ],
  };

  progress = 0;
  process = false;
  showOptions = false;
  downloadReady = false;

  private downloadUrl: string | null = null;

  private http = inject(ObjectDetectionService);

  private readonly translateService = inject(TranslateService);

  constructor(
    private seo: SeoService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.seo.setMetaTitle('Image Recognition - Free & Easy');
    this.seo.setMetaDescription(
      'Experience cutting-edge AI-powered image recognition and object detection with our intuitive Image Recognition App. Effortlessly upload images, adjust settings, and detect objects with high precision. Ideal for developers, businesses, and enthusiasts seeking advanced AI solutions for image analysis.'
    );
  }

  handleUpload(file: any) {
    this.uploadedFile = file;
    this.fileUrl = file.src;
    this.showOptions = true;
  }

  detectObject(): void {
    this.showOptions = false;
    this.process = true;
    this.http
      .DetectAndDrawObjectsBounds(this.uploadedFile.name, this.options)
      .pipe(
        switchMap((res) => this.getStatusWithRetry(res.Payload)),
        filter((res) => res.State === 'Completed')
      )
      .subscribe((res) => {
        this.fileUrl = res.Payload[0].Url;
        this.downloadUrl = res.Payload[0].DownloadUrl;
        this.process = false;
        this.downloadReady = true;
      });
  }

  setOptions(val: DetectionOptions): void {
    this.options = val;
  }

  download(): void {
    const link = document.createElement('a');
    link.href = this.downloadUrl!;
    link.download = this.uploadedFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  getStatusWithRetry(operationId: string, maxRetries = 5000): Observable<any> {
    return this.http.GetStatus(operationId).pipe(
      expand((res) => {
        if (res.State === 'Completed') {
          this.progress = 100;

          return [];
        }

        this.progress = res.Progress;

        return this.http
          .GetStatus(res.OperationId)
          .pipe(delayWhen(() => timer(3000)));
      })
    );
  }
}
